import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { JsonEditor, PrimaryButton, SecondaryButton } from '@/components/index';
import { ACLAuthorizationRequest } from '@/api/bol/models';
import { MemberIdentifierSelect } from './MemberIdentifierSelect';

export interface LogisticsObjectFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { memberIdentifier: string; jsonData: string; aclAuthorizations?: ACLAuthorizationRequest[] }) => Promise<void>;
  title: string; // e.g., "Create New Quotation", "Create New Service"
  jsonFieldName: string; // e.g., "quotationJson", "serviceJson"
  defaultJsonTemplate: string;
  jsonLabel: string; // e.g., "Enter a valid Quotation JSON according to the SDL schema:"
  jsonHelperText: string; // e.g., "The JSON must conform to the Quotation schema defined in the BOL API"
  submitButtonText?: string; // e.g., "Create", "Submit"
}

interface FormData {
  memberIdentifier: string;
  jsonData: string;
  aclAuthorizationsJson: string;
  enableAclAuthorizations: boolean;
}

export const LogisticsObjectFormDialog: React.FC<LogisticsObjectFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  title,
  jsonFieldName,
  defaultJsonTemplate,
  jsonLabel,
  jsonHelperText,
  submitButtonText = "Create"
}) => {
  const [aclExpanded, setAclExpanded] = useState(false);
  const [enableAclAuthorizations, setEnableAclAuthorizations] = useState(false);
  
  const defaultAclTemplate = '[\n  {\n    "memberIdentifier": "",\n    "mode": "READ"\n  }\n]';
  
  const { control, register, handleSubmit, setValue, formState: { errors, isSubmitting }, reset } = useForm<FormData>({
    defaultValues: {
      memberIdentifier: '',
      jsonData: defaultJsonTemplate,
      aclAuthorizationsJson: defaultAclTemplate,
      enableAclAuthorizations: false,
    },
  });

  const handleFormSubmit = async (data: FormData) => {
    try {
      // Parse ACL authorizations if enabled and provided
      let aclAuthorizations: ACLAuthorizationRequest[] | undefined;
      if (data.enableAclAuthorizations && data.aclAuthorizationsJson.trim()) {
        try {
          const parsed = JSON.parse(data.aclAuthorizationsJson);
          // Only include if it's a non-empty array
          if (Array.isArray(parsed) && parsed.length > 0) {
            aclAuthorizations = parsed;
          }
        } catch (e) {
          console.error('Failed to parse ACL authorizations:', e);
          // Continue without ACL authorizations if parsing fails
        }
      }
      
      // Map the generic jsonData back to the expected field name for backward compatibility
      const submissionData = {
        memberIdentifier: data.memberIdentifier,
        [jsonFieldName]: data.jsonData,
        jsonData: data.jsonData, // Also provide as jsonData for newer consumers
        aclAuthorizations
      };
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await onSubmit(submissionData as any);
      reset(); // Reset form fields on successful submission
      setAclExpanded(false); // Reset accordion state
      setEnableAclAuthorizations(false); // Reset ACL state
      onClose();
    } catch (error) {
      // Error handling is done in the parent component/hook
      console.error('Form submission failed:', error);
    }
  };

  const handleClose = () => {
    reset(); // Reset form fields when closing dialog
    setAclExpanded(false); // Reset accordion state
    setEnableAclAuthorizations(false); // Reset ACL state
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }} component="form" onSubmit={handleSubmit(handleFormSubmit)}>
          {/* Member Selection */}
          <Controller
            name="memberIdentifier"
            control={control}
            rules={{ required: 'Member identifier is required' }}
            render={({ field }) => (
              <MemberIdentifierSelect
                value={field.value}
                onChange={field.onChange}
                open={open}
                required
                error={errors.memberIdentifier?.message}
              />
            )}
          />
          
          {/* JSON Editor */}
          <JsonEditor
            {...register('jsonData', { 
              required: `${jsonFieldName} is required`, 
              validate: value => {
                try {
                  JSON.parse(value);
                  return true;
                } catch {
                  return 'Invalid JSON format';
                }
              }
            })}
            value={defaultJsonTemplate}
            onChange={(value: string) => setValue('jsonData', value)}
            height="400px"
            label={jsonLabel}
            error={errors.jsonData?.message}
            helperText={jsonHelperText}
          />
          
          {/* ACL Authorizations (Optional) */}
          <Accordion 
            expanded={aclExpanded} 
            onChange={(_, isExpanded) => setAclExpanded(isExpanded)}
            sx={{ mt: 2 }}
          >
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography>ACL Authorizations (Optional)</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Controller
                name="enableAclAuthorizations"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        {...field}
                        checked={field.value}
                        onChange={(e) => {
                          field.onChange(e);
                          setEnableAclAuthorizations(e.target.checked);
                        }}
                      />
                    }
                    label="Enable ACL Authorizations Header"
                    sx={{ mb: 2 }}
                  />
                )}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Define additional access control authorizations for this logistics object. 
                Each authorization grants a specific member access with a given mode (READ, WRITE, or CONTROL).
                {!enableAclAuthorizations && (
                  <Typography component="span" variant="body2" color="warning.main" sx={{ display: 'block', mt: 1, fontWeight: 'bold' }}>
                    ⚠ The ACL authorizations header will NOT be sent unless the checkbox above is enabled.
                  </Typography>
                )}
              </Typography>
              <JsonEditor
                {...register('aclAuthorizationsJson', { 
                  validate: value => {
                    // Only validate if ACL authorizations are enabled
                    if (!enableAclAuthorizations) return true;
                    if (!value.trim()) return true; // Allow empty
                    try {
                      const parsed = JSON.parse(value);
                      if (!Array.isArray(parsed)) {
                        return 'ACL authorizations must be a JSON array';
                      }
                      // Validate each authorization
                      for (const auth of parsed) {
                        if (!auth.memberIdentifier || typeof auth.memberIdentifier !== 'string') {
                          return 'Each authorization must have a memberIdentifier string';
                        }
                        if (!auth.mode || !['READ', 'WRITE', 'CONTROL'].includes(auth.mode)) {
                          return 'Each authorization must have a mode: READ, WRITE, or CONTROL';
                        }
                      }
                      return true;
                    } catch {
                      return 'Invalid JSON format for ACL authorizations';
                    }
                  }
                })}
                value={defaultAclTemplate}
                onChange={enableAclAuthorizations ? (value: string) => setValue('aclAuthorizationsJson', value) : () => {}}
                height="200px"
                label="ACL Authorizations JSON"
                error={errors.aclAuthorizationsJson?.message}
                helperText="Format: [{ memberIdentifier: string, mode: 'READ' | 'WRITE' | 'CONTROL' }]"
              />
            </AccordionDetails>
          </Accordion>
        </Box>
      </DialogContent>
      <DialogActions>
        <SecondaryButton onClick={handleClose} disabled={isSubmitting}>Cancel</SecondaryButton>
        <PrimaryButton 
          onClick={handleSubmit(handleFormSubmit)} 
          disabled={isSubmitting}
        >
          {submitButtonText}
        </PrimaryButton>
      </DialogActions>
    </Dialog>
  );
};